import React, { useState } from 'react'
import { useHistory } from 'react-router'
import { connect } from 'react-redux'
import ProgramHeader from '../ReusableComponents/Header/ProgramHeader'
import Footer from '../ReusableComponents/FooterComponent'
import DialogModal from '../ReusableComponents/DialogModal/DialogModal'
import Toast from '../ReusableComponents/UI/Toast'
import { clearAiProject } from './aiProjectStore'
import {
    getAiCodeCategories,
    getAiCodeFeatures,
} from '../../hooks/useBrandConfig'
import { getAiCodeIcon } from './aiCodeIcons'
import {
    requiredInputFor,
    inputLabel,
    dependencyMet,
} from './aiFeatureDependency'
import styles from './AiCode.module.css'

const SELECTED_FEATURES_KEY = 'aiCodeSelectedFeatures'

const loadSelectedFeatures = () => {
    try {
        const saved = sessionStorage.getItem(SELECTED_FEATURES_KEY)
        return saved ? JSON.parse(saved) : []
    } catch {
        return []
    }
}

// Vision-based A.I. features run off the camera, so they need image training.
const VISION_AI_FEATURES = [
    'ai/motion',
    'ai/color',
    'ai/face',
    'ai/object',
    'ai/pose',
    'ai/gesture',
    'ai/tracking',
    'ai/ocr',
    'ai/marker',
]

// Translate the AI-code selection into the same `enable` object the AppMode
// training/programming pipeline consumes ({ s1: {cam, mic, pc, remote} }), so
// the Configure (TrainMachine) and Code steps can be reused as-is.
const buildEnableFromSelection = (selected) => {
    const has = (key) => selected.includes(key)
    const cam = has('inputs/camera') || VISION_AI_FEATURES.some(has)
    const mic = has('inputs/microphone') || has('ai/voice')
    const pc = selected.some((k) => k.startsWith('outputs/'))
    return {
        s1: { cam, mic, pc, remote: false },
        s2: { face: false, emotion: false, object: false },
    }
}

const AiCode = () => {
    const history = useHistory()
    const categories = getAiCodeCategories()
    // Selected features persist across sections and navigation so a choice
    // made in Inputs is still remembered.
    const [selectedFeatures, setSelectedFeatures] = useState(
        loadSelectedFeatures
    )
    const [toast, setToast] = useState('')
    // Going back to the project screen discards the unsaved studio; confirm
    // first, and on YES wipe all AI state (models, session, sidebar) to default.
    const [showEraseModal, setShowEraseModal] = useState(false)
    const headerData = {
        selected: 0,
        options: [
            <p key="select-feature">Select Feature</p>,
            <p key="configure">Configure</p>,
            <p key="code">Code</p>,
            <p key="execute">Execute</p>,
        ],
    }

    // Toast auto-dismisses itself (see the Toast component); just set the text.
    const showToast = (message) => setToast(message)

    const persistSelectedFeatures = (next) => {
        // Dropping an input also drops any A.I. feature that depends on it, so
        // an unusable selection can never be carried forward.
        const cleaned = next.filter((k) => {
            const [cat, slug] = k.split('/')
            return dependencyMet(cat, slug, next)
        })
        setSelectedFeatures(cleaned)
        sessionStorage.setItem(SELECTED_FEATURES_KEY, JSON.stringify(cleaned))

        // Drop any placed output widgets whose output type is no longer selected,
        // so the Output screen doesn't keep deselected widgets (bug 29).
        try {
            // Button/Toggle are on-screen widgets that live under Inputs now, so
            // keep them alongside the display outputs when pruning stale widgets.
            const enabledOutputTypes = cleaned
                .filter(
                    (k) =>
                        k.startsWith('outputs/') ||
                        k === 'inputs/button' ||
                        k === 'inputs/toggle'
                )
                .map((k) => k.split('/')[1])
            const outs =
                JSON.parse(sessionStorage.getItem('aiCodeOutputs')) || []
            const kept = outs.filter((o) =>
                enabledOutputTypes.includes(o.type)
            )
            if (kept.length !== outs.length)
                sessionStorage.setItem('aiCodeOutputs', JSON.stringify(kept))
        } catch {
            /* ignore */
        }
    }

    const handleFeatureClick = (categorySlug, feature) => {
        if (feature.enabled === false) {
            showToast(`${feature.display_name} is coming soon...`)
            return
        }
        const key = `${categorySlug}/${feature.slug}`
        const isSelected = selectedFeatures.includes(key)
        // Block selecting an A.I. feature whose required input isn't chosen.
        if (!isSelected) {
            const req = requiredInputFor(categorySlug, feature.slug)
            if (req && !selectedFeatures.includes(req)) {
                showToast(
                    `Select ${inputLabel(req)} input first to use ${feature.display_name
                    }`
                )
                return
            }
        }
        const next = isSelected
            ? selectedFeatures.filter((k) => k !== key)
            : [...selectedFeatures, key]
        persistSelectedFeatures(next)
    }

    const goBack = () => {
        setShowEraseModal(true)
    }
    const navigateWithReload = (path) => {
        const isHashRouter = window.location.hash.startsWith('#/')

        if (isHashRouter) {
            window.location.assign(`/#${path}`)
        } else {
            window.location.assign(path)
        }
    }
    const confirmErase = async (choice) => {
        setShowEraseModal(false)
        if (choice !== 'Yes') return
        // Reset every AI-owned thing to default: trained models, session keys,
        // sidebar tab, counters. A hard navigation reloads redux from the now
        // cleared storage so nothing stale (e.g. camModels) lingers in memory.
        try {
            await clearAiProject()
        } catch (e) {
            /* ignore — still navigate away */
        }
        sessionStorage.setItem('slideDirection', true)
        // Full reload (not a soft SPA push) so redux re-initialises from the now
        // cleared storage — otherwise trained class names/models linger in memory
        // and still show in Configure after the erase.
        navigateWithReload('/ai-project')
    }

    const goNext = () => {
        if (!selectedFeatures.length) {
            showToast('Please select at least one feature')
            return
        }
        // A.I. Studio needs at least one A.I. capability — inputs/outputs alone
        // aren't enough to build with, so require an `ai/*` feature.
        const hasAiFeature = selectedFeatures.some((k) => k.startsWith('ai/'))
        if (!hasAiFeature) {
            showToast('Please select at least one A.I. feature')
            return
        }
        // Reuse the AppMode training/programming pipeline by expressing the
        // selection as its `enable` object, and mark the AI-code context so
        // those screens can return here.
        const enable = buildEnableFromSelection(selectedFeatures)
        sessionStorage.setItem('enable', JSON.stringify(enable))
        sessionStorage.setItem('webAiMode', 'true')

        // Configure = training, only when a feature that needs it is selected:
        // camera / vision-AI -> image training, mic / voice -> audio training.
        // Everything else skips straight to the Code step. These are dedicated
        // AI-code routes (not the AppMode ones) so the ProgramHeader is kept.
        if (enable.s1.cam) {
            history.push('/aiCodeConfigure')
        } else if (enable.s1.mic) {
            history.push('/aiCodeConfigureAudio')
        } else {
            history.push('/aiCodeFlow')
        }
    }

    return (
        <div className={styles.container}>
            <DialogModal
                show={showEraseModal}
                text="All Unsaved program will be Erased. Are you sure want to continue ?"
                showCloseBtn={false}
                optionsToSelect={[
                    { text: 'YES', do: () => confirmErase('Yes') },
                    { text: 'NO', do: () => confirmErase('No') },
                ]}
            />
            <ProgramHeader data={headerData} showSave={true} showHelp={true} />

            {/* One screen: every category as a titled section with a divider,
                so there's no sidebar and less empty space. */}
            <div className={styles.body}>
                {categories.map((category) => {
                    const feats = [
                        ...getAiCodeFeatures(category.slug),
                    ].sort(
                        (a, b) => (b.enabled !== false) - (a.enabled !== false)
                    )
                    if (!feats.length) return null
                    return (
                        <section
                            key={category.slug}
                            className={styles.section}
                        >
                            <div className={styles.sectionHead}>
                                <h3 className={styles.sectionTitle}>
                                    {category.display_name}
                                </h3>
                                <span className={styles.sectionRule} />
                            </div>
                            <div className={styles.grid}>
                                {feats.map((feature) => {
                                    const Icon = getAiCodeIcon(
                                        category.slug,
                                        feature.icon
                                    )
                                    const req = requiredInputFor(
                                        category.slug,
                                        feature.slug
                                    )
                                    const depMet = dependencyMet(
                                        category.slug,
                                        feature.slug,
                                        selectedFeatures
                                    )
                                    const disabled =
                                        feature.enabled === false || !depMet
                                    const isSelected = selectedFeatures.includes(
                                        `${category.slug}/${feature.slug}`
                                    )
                                    return (
                                        <div
                                            key={feature.slug}
                                            className={`${styles.card} ${disabled
                                                ? styles.cardDisabled
                                                : ''
                                                } ${isSelected
                                                    ? styles.cardSelected
                                                    : ''
                                                }`}
                                            onClick={() =>
                                                handleFeatureClick(
                                                    category.slug,
                                                    feature
                                                )
                                            }
                                            title={
                                                !depMet
                                                    ? `Requires ${inputLabel(
                                                        req
                                                    )} input`
                                                    : undefined
                                            }
                                        >
                                            <div className={styles.cardIcon}>
                                                {Icon && (
                                                    <Icon
                                                        width={40}
                                                        height={40}
                                                        className={styles.icon}
                                                    />
                                                )}
                                            </div>
                                            <p className={styles.cardLabel}>
                                                {feature.display_name}
                                            </p>
                                            {!depMet &&
                                                feature.enabled !== false && (
                                                    <p
                                                        className={
                                                            styles.cardHint
                                                        }
                                                    >
                                                        Needs {inputLabel(req)}
                                                    </p>
                                                )}
                                        </div>
                                    )
                                })}
                            </div>
                        </section>
                    )
                })}
            </div>

            <div className={styles.footer}>
                <Footer
                    isGoBack={true}
                    goBack={goBack}
                    isGoNext={true}
                    goNext={goNext}
                />
            </div>

            <Toast
                show={!!toast}
                message={toast}
                duration={2200}
                onClose={() => setToast('')}
            />
        </div>
    )
}

const mapStateToProps = (state) => state
export default connect(mapStateToProps)(AiCode)
